// utils/exportPng.ts
import { toPng } from 'html-to-image'

/**
 * DOM-capture (html-to-image) only sees each element's VISIBLE box, so any content inside an `overflow: auto`
 * scroll viewport — e.g. the Jaccard overlap heatmap's grid, or the OTU table — gets cropped to whatever was on
 * screen (SPCFY-BACKEND-MICRONAUT-95: "when I try to export it to a png, the view partly cut off"). Before
 * capturing, temporarily release every clipping container to its full scroll size + `overflow: visible`, so the
 * whole widget lands in the PNG. Iterated to a fixed point because releasing an inner scroll pane can make its
 * (previously non-clipping) fixed-height wrapper start to overflow too. A canvas chart that already fits its box
 * has no scroll overflow, so this is a no-op for it. Everything is restored in `finally`.
 */
function expandClippersForCapture(root: HTMLElement): () => void {
  const touched: Array<{ el: HTMLElement; prev: string | null }> = []
  const release = (el: HTMLElement) => {
    touched.push({ el, prev: el.getAttribute('style') })
    el.style.overflow = 'visible'
    el.style.maxHeight = 'none'
    el.style.maxWidth = 'none'
    el.style.height = `${el.scrollHeight}px`
    el.style.width = `${el.scrollWidth}px`
  }
  for (let pass = 0; pass < 5; pass++) {
    const clippers = [root, ...Array.from(root.querySelectorAll<HTMLElement>('*'))]
      .filter((el) => !touched.some((t) => t.el === el))
      .filter((el) => el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1)
    if (clippers.length === 0) break
    clippers.forEach(release)
  }
  return () => {
    // Restore in reverse so nested writes unwind cleanly.
    for (let i = touched.length - 1; i >= 0; i--) {
      const { el, prev } = touched[i]
      if (prev === null) el.removeAttribute('style')
      else el.setAttribute('style', prev)
    }
  }
}

export async function exportElementAsPng(
  node: HTMLElement,
  filename = 'widget.png',
  opts: { pixelRatio?: number; backgroundColor?: string; padding?: number } = {}
) {
  const { pixelRatio = Math.max(2, Math.ceil(window.devicePixelRatio || 1)), backgroundColor = '#ffffff', padding = 0 } = opts

  const restore = expandClippersForCapture(node)
  try {
    const dataUrl = await toPng(node, {
      skipFonts: true,
      cacheBust: true,
      pixelRatio,
      backgroundColor,
      // content-box (not border-box) so the padding adds a margin around the full content instead of shrinking
      // the content area back under a just-expanded element and re-cropping it.
      style: padding ? { padding: `${padding}px`, boxSizing: 'content-box', backgroundColor } : { backgroundColor }
    })

    const anchorElement = document.createElement('a')
    anchorElement.href = dataUrl
    anchorElement.download = filename
    anchorElement.click()
  } finally {
    restore()
  }
}
